import svgPaths from '@/assets/svg-icons';

export const ToastStatus = {
  ERROR: 'error',
  LOADING: 'loading',
  SUCCESS: 'success',
} as const;

export type ToastStatusKey = (typeof ToastStatus)[keyof typeof ToastStatus];

export const ProgramCodes = {
  FILE_LIBRARY: 'fileLibrary',
  RESOURCE_REQUEST: 'resourceRequest',
  PATIENT_TRANSFER: 'patientTransfer',
  MOD: 'mod',
  FAQ: 'faq',
  HOSPITAL: 'hospital',
} as const;

export type ProgramCodeKey = (typeof ProgramCodes)[keyof typeof ProgramCodes];

export const ProgramTitles = {
  FILE_LIBRARY: 'File Library',
  RESOURCE_REQUEST: 'Resource Request',
  PATIENT_TRANSFER: 'Patient Transfer',
  MOD: 'Message of the Day',
  FAQ: 'Frequently Asked Questions',
  HOSPITAL: 'Hospital',
} as const;

export type ProgramTitleKey = (typeof ProgramTitles)[keyof typeof ProgramTitles];

export const SpecialPermission = {
  COORDINATOR: 'C',
  ADMINISTRATOR: 'A',
  READ_ONLY: 'R',
  HOSPITAL: 'H',
};

export type SpecialPermissionKey = (typeof SpecialPermission)[keyof typeof SpecialPermission];

export const RequestStatus = {
  REQUEST: 'Request',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  RECEIVED: 'Received',
  CANCELLED: 'Cancelled',
  FORWARDED: 'Forwarded',
  PARTIAL: 'Partial',
  DENIED: 'Denied',
};

export type RequestStatusKey = (typeof RequestStatus)[keyof typeof RequestStatus];

export const TransferStatusEnum = {
  REQUEST: 'Request',
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  CANCELLED: 'Cancelled',
  ONHOLD: 'On Hold',
  PARTIAL: 'Partially Accepted',
  RELEASED: 'Released',
};

export type TransferStatusKey = (typeof TransferStatusEnum)[keyof typeof TransferStatusEnum];

export const FulfillmentDeliveryStatus = {
  WILL_DELIVER: 'Willing to deliver',
  NOT_DELIVER: 'Unable to deliver',
};

export type FulfillmentDeliveryStatusKey =
  (typeof FulfillmentDeliveryStatus)[keyof typeof FulfillmentDeliveryStatus];

export const FulfillmentStatus = {
  ACCEPTED: 'Accepted',
  DENIED: 'Denied',
  PENDING: 'Pending',
};

export type FulfillmentStatusKey = (typeof FulfillmentStatus)[keyof typeof FulfillmentStatus];

export const PTRequestStepForm = {
  RECIPIENTS: 'recipients',
  CONTENT: 'content',
};

export type PTRequestStepFormKey = (typeof PTRequestStepForm)[keyof typeof PTRequestStepForm];

export const UserStatus = {
  APPROVED: 'Approved',
  SUSPENDED: 'Suspended',
  PENDING: 'Pending Approval',
  NOT_APPROVED: 'Not Approved',
};

export type UserStatusKey = (typeof UserStatus)[keyof typeof UserStatus];

export const AreaName = {
  PEE_DEE_COUNTIES: 'Pee Dee',
  LOW_COUNTY_COUNTIES: 'Lowcountry',
  MIDLANDS_COUNTIES: 'Midlands',
  UPSTATE_COUNTIES: 'Upstate',
};

export type AreaNameKey = (typeof AreaName)[keyof typeof AreaName];

export const SVGIconName = {
  CARD: 'card',
  SEARCH: 'search',
  CHECKED: 'checked',
  BIG_X: 'bigX',
  MENU: 'menu',
  CROSS: 'cross',
  WEB_ASSET: 'webAsset',
  PERSON: 'person',
  CLOSE: 'close',
  INFO: 'info',
  CHECK_OUTLINED: 'checkOutlined',
  CONTACT: 'contact',
  INFO_OUTLINED: 'infoOutlined',
};

export type SVGIconNameKey = keyof typeof svgPaths;

export const ForgotFormSteps = {
  STEP1: 1,
  STEP2: 2,
  STEP3: 3,
};

export const RegionalCoordinatorArea = {
  LOWCOUNTRY: 'Lowcountry',
  MIDLANDS: 'Midlands',
  UPSTATE: 'Upstate',
  PEE_DEE: 'Pee Dee',
} as const;

export type RegionalCoordinatorAreaKey =
  (typeof RegionalCoordinatorArea)[keyof typeof RegionalCoordinatorArea];

export const AppStatusEnum = {
  GRANT: 'grant',
  REVOKE: 'revoke',
} as const;

export type AppStatusEnumKey = (typeof AppStatusEnum)[keyof typeof AppStatusEnum];

export const PatientTransferType = {
  INDIVIDUAL: 'individual',
  BULK: 'bulk',
};

export type PatientTransferTypeKey = (typeof PatientTransferType)[keyof typeof PatientTransferType];

export const BlastEmailStepForm = {
  RECIPIENTS: 'recipients',
  CONTENT: 'content',
};

export type BlastEmailStepFormKey = (typeof BlastEmailStepForm)[keyof typeof BlastEmailStepForm];

export const BlastEmailMessageTypeEnum = {
  MESSAGE: 'message',
  CONTACTS: 'contacts',
  BED_COUNTS: 'bed_counts',
} as const;

// Which "Blast email" action opened OrgBlastEmailModal. Drives the modal's
// mode-specific defaults: DETAILS is today's editable behavior; BED_COUNT locks
// Organization Type to Hospital and preselects the Bed Counts message template.
export const OrgBlastEmailContext = {
  DETAILS: 'details',
  BED_COUNT: 'bed_count',
} as const;

export type OrgBlastEmailContextKey =
  (typeof OrgBlastEmailContext)[keyof typeof OrgBlastEmailContext];

export const PTBlastEmailMessageTypeEnum = {
  HURRICANE: 'hurricane',
  LOCAL_REGIONAL: 'local_regional',
} as const;

export const OrganizationFormTab = {
  DETAILS: 'Details',
  INVENTORY: 'Inventory',
};

export type OrganizationFormTabKey = (typeof OrganizationFormTab)[keyof typeof OrganizationFormTab];

export const InventoryResourceType = {
  EQUIPMENT: 'Equipment',
  COMMODITY: 'Commodity',
};

export const HospitalStatus = {
  NORMAL_OPERATIONS: 'Normal Operations',
  UNDER_CONTROL: 'Under Control',
  LIMITED_SERVICES: 'Limited Services',
  IMPAIRED_SERVICES: 'Impaired Services',
} as const;

export type HospitalStatusKey = (typeof HospitalStatus)[keyof typeof HospitalStatus];

// A hospital-only sub-classification -- what *kind* of hospital this is. Not `facilityType`,
// the organization-type column the org form edits one section above under the label
// "Organization"; the two sit within one screen of each other, which is why the portal's label
// keeps all three words rather than shortening to "Facility type".
//
// Byte-for-byte match with dph-portal-api's HOSPITAL_FACILITY_TYPE_OPTIONS
// (src/constants/index.ts) -- keep both in sync by hand, the same way `BedTypeOptions` in
// src/common/constants/index.ts is kept in sync with PATIENT_BED_TYPES. That constant is the
// enum on PostOrganizationSchema's `hospitalFacilityType` property, serialized into an
// AWS::ApiGateway::Model, so a one-character difference is a gateway 400 ahead of any handler
// and the response names no field -- it fails the whole organization save. Adding a value here
// means adding it there first and deploying that side before this one.
//
// `Free Standing ED` is three words with no hyphen.
export const HospitalFacilityType = {
  ACUTE_CARE: 'Acute Care',
  BEHAVIORAL_HEALTH: 'Behavioral Health',
  FREE_STANDING_ED: 'Free Standing ED',
  INFIRMARY: 'Infirmary',
  REHAB: 'Rehab',
} as const;

export type HospitalFacilityTypeKey =
  (typeof HospitalFacilityType)[keyof typeof HospitalFacilityType];

// Keep in sync with HospitalBedCount.category (src/interfaces/interface.d.ts) by hand -- that
// file is an ambient global, so a *top-level* import there would take every interface in it out
// of global scope. That restriction is narrower than it looks: an inline `import('common/enum')`
// type does not trigger module detection, which is how `PocNotificationRoleValue` in the same
// file reaches its enum without restating the literals. Hand-syncing here is inherited, not
// forced -- see that block before adding another by-hand copy.
export const HospitalBedCategory = {
  CRITICAL_CARE_ICU: 'Critical Care / ICU (Non-Pediatric)',
  INTERMEDIATE_CARE: 'Intermediate Care',
  MEDICAL_SURGICAL: 'Medical/Surgical',
  BURN: 'Burn',
  PEDIATRIC: 'Pediatric (Includes Newborn Nursery Beds)',
  OTHER: 'Other',
  COVID_19: 'COVID-19',
  EMERGENCY_DEPARTMENT: 'Emergency Department',
  NEGATIVE_PRESSURE: 'Negative Pressure (ALL)',
  REHABILITATION: 'Rehabilitation',
  PSYCH: 'Psych (Includes Substance Abuse Beds)',
  LABOR_AND_DELIVERY: 'Labor and Delivery',
  NURSERY: 'Nursery',
  DIALYSIS_SERVICES: 'Dialysis Services (ALL)',
  PEDIATRIC_ICU: 'Pediatric ICU (Includes NICU Beds)',
} as const;

export type HospitalBedCategoryKey = (typeof HospitalBedCategory)[keyof typeof HospitalBedCategory];

// The per-event notification role a point of contact holds. All three receive the event's
// email; PRIMARY and SECONDARY additionally name the contact surfaced on the facility's
// contact cards. `null` means the contact is not subscribed to that event at all.
//
// Values match the `ENUM('NOTIFY','PRIMARY','SECONDARY')` columns on `healthcare_poc`, and
// the API's backfill maps a pre-existing flag of `true` to `NOTIFY` -- which is why ticking a
// row's checkbox in the contact modal sets `NOTIFY` rather than a ranked role.
//
// Unlike `HospitalBedCategory` above, this one is NOT hand-synced into the ambient interfaces:
// `src/interfaces/interface.d.ts` reaches it through an inline `import()` type, which does not
// turn that file into a module. See the alias there before restating these literals anywhere.
export const PocNotificationRole = {
  NOTIFY: 'NOTIFY',
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
} as const;

export type PocNotificationRoleKey = (typeof PocNotificationRole)[keyof typeof PocNotificationRole];
